import { useState } from "react";
import {
  Button,
  Link,
  MessageBar,
  MessageBarBody,
  ProgressBar,
  Select,
  Spinner,
  Tab,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Add24Regular, ArrowDownload20Regular, Delete20Regular, Document16Regular, Edit20Regular, TaskListLtr24Regular } from "@fluentui/react-icons";
import { PLICHI, STATI_DOCUMENTO, type StatoDocumento } from "@app/schema";
import { useData } from "../data/DataProvider";
import { loadLotti, oggettoGara, type Lotto } from "../data/gare";
import { avanzamento, componenti, eliminaVoce, loadChecklist, ordinaVoci, salvaVoce, vociDi, type VoceChecklist } from "../data/checklist";
import { creaExcelChecklist, scarica } from "../data/excel";
import { useAsync } from "../lib/useAsync";
import { formatRelative } from "../lib/format";
import { goBack } from "../lib/nav";
import { Shell } from "../components/Shell";
import { Section } from "../components/Section";
import { StatoDocumentoBadge } from "../components/Badges";
import { VoceDialog } from "../components/VoceDialog";
import { PreparaChecklistDialog } from "../components/PreparaChecklistDialog";
import { ErrorState, describeError } from "../components/ErrorState";

const useStyles = makeStyles({
  head: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXS },
  oggetto: { margin: 0, fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold },
  meta: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  stati: { display: "flex", gap: tokens.spacingHorizontalS, flexWrap: "wrap", alignItems: "center" },
  vuota: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: tokens.spacingVerticalM },
  doc: { display: "flex", flexDirection: "column", gap: "2px", padding: `${tokens.spacingVerticalXS} 0` },
  link: { display: "inline-flex", alignItems: "center", gap: "4px", overflowWrap: "anywhere" },
  colDoc: { width: "30%" },
  stato: { width: "150px" },
  colFile: { width: "20%" },
  colMod: { width: "170px" },
  azioni: { whiteSpace: "nowrap", width: "84px" },
});

/** Checklist dei documenti della gara (o di ciascun lotto). */
export function Checklist({ id }: { id: number }) {
  const s = useStyles();
  const data = useData();
  const puoModificare = data.roles.gestore || data.roles.collaboratore;
  const gara = useAsync(() => data.gare.get(id).then((x) => x ?? null), [data, id]);
  const lotti = useAsync(() => loadLotti(data, id), [data, id]);
  const voci = useAsync(() => loadChecklist(data, id), [data, id]);
  const [lottoId, setLottoId] = useState<number>();
  const [dialog, setDialog] = useState<{ tipo: "voce"; voce?: VoceChecklist } | { tipo: "modello" }>();
  const [msg, setMsg] = useState<{ intent: "error" | "success"; text: string }>();
  const [salvando, setSalvando] = useState<number>();

  const g = gara.data;
  const elencoLotti = lotti.data ?? [];
  const lotto: Lotto | undefined = g?.fields.PerLotti ? (elencoLotti.find((l) => l.id === lottoId) ?? elencoLotti[0]) : undefined;
  const cartellaId = g?.fields.PerLotti ? lotto?.fields.CartellaId : g?.fields.CartellaId;
  const cartella = useAsync(() => (cartellaId ? data.repository.get(cartellaId).catch(() => undefined) : Promise.resolve(undefined)), [data, cartellaId]);

  if (gara.error || lotti.error || voci.error) return <Pagina titolo="Checklist" errore={gara.error ?? lotti.error ?? voci.error} />;
  if (g === null) return <Pagina titolo="Checklist" errore={new Error("Gara non trovata.")} />;
  if (!g || !lotti.data || !voci.data) return <Pagina titolo="Checklist" />;

  const tutte = voci.data;
  const soggetti = componenti(g);
  const correnti = ordinaVoci(vociDi(tutte, lotto), soggetti);
  const av = avanzamento(correnti);
  const lottiMancanti = g.fields.PerLotti && !elencoLotti.length;

  async function cambiaStato(v: VoceChecklist, stato: StatoDocumento) {
    setSalvando(v.id);
    setMsg(undefined);
    try {
      await salvaVoce(data, v, { Stato: stato });
    } catch (e) {
      setMsg({ intent: "error", text: describeError(e).detail });
    } finally {
      await voci.reload();
      setSalvando(undefined);
    }
  }

  async function elimina(v: VoceChecklist) {
    if (!window.confirm(`Togliere "${v.fields.Title}" dalla checklist?`)) return;
    try {
      await eliminaVoce(data, v);
    } catch (e) {
      setMsg({ intent: "error", text: describeError(e).detail });
    }
    await voci.reload();
  }

  async function esporta() {
    const fogli = g!.fields.PerLotti
      ? elencoLotti.map((l) => ({ lotto: l, voci: ordinaVoci(vociDi(tutte, l), soggetti) })).filter((f) => f.voci.length)
      : [{ voci: correnti }];
    scarica(await creaExcelChecklist(g!, fogli), `Checklist_${g!.fields.Codice ?? g!.id}.xlsx`);
  }

  return (
    <Shell
      title={`Checklist · ${g.fields.Codice ?? `Gara ${g.id}`}`}
      onBack={() => goBack(`/g/${g.id}`)}
      actions={
        <>
          <Button appearance="subtle" icon={<ArrowDownload20Regular />} onClick={() => void esporta()} disabled={!tutte.length}>
            Excel
          </Button>
          {puoModificare && !lottiMancanti && (
            <>
              <Button appearance="subtle" icon={<TaskListLtr24Regular />} onClick={() => setDialog({ tipo: "modello" })}>
                Dal modello
              </Button>
              <Button appearance="primary" icon={<Add24Regular />} onClick={() => setDialog({ tipo: "voce" })}>
                Documento
              </Button>
            </>
          )}
        </>
      }
    >
      <div className={s.head}>
        <h1 className={s.oggetto}>{oggettoGara(g)}</h1>
        <span className={s.meta}>
          {g.fields.Stazione} · {soggetti.length > 1 ? `RTI: ${soggetti.join(", ")}` : soggetti[0]}
        </span>
      </div>

      {msg && (
        <MessageBar intent={msg.intent}>
          <MessageBarBody>{msg.text}</MessageBarBody>
        </MessageBar>
      )}

      {g.fields.PerLotti && elencoLotti.length > 0 && (
        <TabList selectedValue={lotto?.id} onTabSelect={(_, d) => setLottoId(d.value as number)}>
          {elencoLotti.map((l) => {
            const a = avanzamento(vociDi(tutte, l));
            return (
              <Tab key={l.id} value={l.id}>
                Lotto {l.fields.Numero}
                {a.totale > 0 && <span className={s.meta}> · {a.pronti}/{a.totale}</span>}
              </Tab>
            );
          })}
        </TabList>
      )}

      {lottiMancanti ? (
        <Section>
          <span className={s.meta}>La gara è a lotti: la checklist si prepara per ciascun lotto. Aggiungi prima i lotti dalla scheda della gara.</span>
        </Section>
      ) : correnti.length === 0 ? (
        <Section>
          <div className={s.vuota}>
            <span>{lotto ? `La checklist del lotto ${lotto.fields.Numero} è vuota.` : "La checklist è vuota."}</span>
            {puoModificare ? (
              <>
                <span className={s.meta}>Parti dai documenti che si chiedono di solito, poi aggiungi o togli quello che serve per questa gara.</span>
                <Button appearance="primary" icon={<TaskListLtr24Regular />} onClick={() => setDialog({ tipo: "modello" })}>
                  Prepara dal modello
                </Button>
              </>
            ) : null}
          </div>
        </Section>
      ) : (
        <>
          <Section>
            <div className={s.stati}>
              <strong>
                {av.pronti} di {av.totale} pronti
              </strong>
              <span className={s.meta}>(definitivi o approvati)</span>
            </div>
            <ProgressBar value={av.totale ? av.pronti / av.totale : 0} thickness="large" color={av.pronti === av.totale ? "success" : "brand"} />
            <div className={s.stati}>
              {STATI_DOCUMENTO.filter((x) => av.perStato[x]).map((x) => (
                <span key={x} className={s.stati}>
                  <StatoDocumentoBadge stato={x} /> <span className={s.meta}>{av.perStato[x]}</span>
                </span>
              ))}
            </div>
          </Section>

          {PLICHI.map((p) => {
            const righe = correnti.filter((v) => (v.fields.Plico ?? "Documentazione Amministrativa") === p);
            if (!righe.length) return null;
            return (
              <Section key={p} title={`${p} · ${righe.length}`}>
                <Table size="small" aria-label={p} style={{ tableLayout: "fixed" }}>
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell className={s.colDoc}>Documento</TableHeaderCell>
                      <TableHeaderCell className={s.stato}>Stato</TableHeaderCell>
                      <TableHeaderCell className={s.colFile}>File</TableHeaderCell>
                      <TableHeaderCell>Note</TableHeaderCell>
                      <TableHeaderCell className={s.colMod}>Modificato</TableHeaderCell>
                      {puoModificare && <TableHeaderCell className={s.azioni} />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {righe.map((v) => (
                      <TableRow key={v.id}>
                        <TableCell>
                          <div className={s.doc}>
                            <span>{v.fields.Title}</span>
                            {v.fields.Soggetto && <span className={s.meta}>{v.fields.Soggetto}</span>}
                          </div>
                        </TableCell>
                        <TableCell className={s.stato}>
                          {puoModificare ? (
                            <Select
                              size="small"
                              aria-label={`Stato di ${v.fields.Title}`}
                              value={v.fields.Stato ?? "da fare"}
                              disabled={salvando === v.id}
                              onChange={(_, d) => void cambiaStato(v, d.value as StatoDocumento)}
                            >
                              {STATI_DOCUMENTO.map((x) => (
                                <option key={x}>{x}</option>
                              ))}
                            </Select>
                          ) : (
                            <StatoDocumentoBadge stato={v.fields.Stato} />
                          )}
                        </TableCell>
                        <TableCell>
                          {v.fields.FileUrl ? (
                            <Link className={s.link} href={v.fields.FileUrl} target="_blank" rel="noreferrer">
                              <Document16Regular /> {v.fields.FileNome || "Apri"}
                            </Link>
                          ) : (
                            <span className={s.meta}>—</span>
                          )}
                        </TableCell>
                        <TableCell>{v.fields.Note ?? ""}</TableCell>
                        <TableCell>
                          <span className={s.meta}>
                            {formatRelative(v.modifiedAt)}
                            {v.modifiedBy?.displayName ? ` · ${v.modifiedBy.displayName}` : ""}
                          </span>
                        </TableCell>
                        {puoModificare && (
                          <TableCell className={s.azioni}>
                            <Button size="small" appearance="subtle" icon={<Edit20Regular />} aria-label={`Modifica ${v.fields.Title}`} onClick={() => setDialog({ tipo: "voce", voce: v })} />
                            <Button size="small" appearance="subtle" icon={<Delete20Regular />} aria-label={`Togli ${v.fields.Title}`} onClick={() => void elimina(v)} />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Section>
            );
          })}
        </>
      )}

      {dialog?.tipo === "voce" && (
        <VoceDialog
          gara={g}
          lotto={lotto}
          voce={dialog.voce}
          voci={correnti}
          soggetti={soggetti}
          cartella={cartella.data}
          onClose={(saved) => {
            setDialog(undefined);
            if (saved) void voci.reload();
          }}
        />
      )}
      {dialog?.tipo === "modello" && (
        <PreparaChecklistDialog
          gara={g}
          lotti={elencoLotti}
          lotto={lotto}
          voci={tutte}
          onClose={(saved) => {
            setDialog(undefined);
            if (saved) {
              void gara.reload();
              void voci.reload();
            }
          }}
        />
      )}
    </Shell>
  );
}

function Pagina({ titolo, errore }: { titolo: string; errore?: unknown }) {
  return (
    <Shell title={titolo} onBack={() => goBack()}>
      {errore ? <ErrorState error={errore} /> : <Spinner style={{ marginTop: 48 }} />}
    </Shell>
  );
}
